import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { heroArrowLeft, heroCheck, heroExclamationTriangle } from '@ng-icons/heroicons/outline';
import {
  ToastrService,
  UiButton,
  UiDateField,
  UiDetailPageSkeleton,
  UiEmptyState,
  UiHint,
  UiInput,
  UiLabel,
  UiPageHeader,
  UiPanelCard,
  UiPanelText,
  UiPanelTitle,
} from '../../../shared/ui';
import { SalesApiService } from './sales-api.service';
import type { SaleListItem, SaleStatus } from './sales.types';

interface SaleDetailResponse {
  sale: SaleListItem;
  lineItems: Array<{ id: string; data: { quantity: number; unit_amount: string; subtotal: string } }>;
}

const EDITABLE_STATUSES: SaleStatus[] = ['draft', 'pending', 'active', 'trialing'];

const STATUS_LABEL_PT_BR: Record<SaleStatus, string> = {
  draft: 'Rascunho',
  pending: 'Pendente',
  active: 'Ativa',
  trialing: 'Em trial',
  paused: 'Pausada',
  past_due: 'Em atraso',
  cancelled: 'Cancelada',
  refunded: 'Reembolsada',
  expired: 'Expirada',
};

@Component({
  selector: 'app-sales-editar-page',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    NgIcon,
    UiButton,
    UiDateField,
    UiDetailPageSkeleton,
    UiEmptyState,
    UiHint,
    UiInput,
    UiLabel,
    UiPageHeader,
    UiPanelCard,
    UiPanelText,
    UiPanelTitle,
  ],
  providers: [provideIcons({ heroArrowLeft, heroCheck, heroExclamationTriangle })],
  template: `
    <section class="catalog-shell">
      <app-ui-page-header
        title="Editar venda"
        subtitle="Vendas gerenciadas por provedor externo não podem ser editadas aqui."
        [divider]="false"
      >
        <div actions class="flex flex-wrap gap-2">
          <a appButton variant="ghost" routerLink="/vendas">
            <ng-icon name="heroArrowLeft" size="14" /> Voltar
          </a>
        </div>
      </app-ui-page-header>

      @if (loading()) {
        <app-ui-detail-page-skeleton titleWidth="32%" subtitleWidth="44%" [blockHeight]="220" />
      } @else if (!sale()) {
        <app-ui-empty-state
          icon="heroExclamationTriangle"
          title="Venda não encontrada"
          description="A venda solicitada não existe ou não está mais disponível."
        />
      } @else if (sale()!.managementType === 'provider_managed') {
        <div appUiPanelCard class="banner banner--warn" role="status">
            <ng-icon name="heroExclamationTriangle" size="16" />
            <div>
              <strong>Venda gerenciada pelo provedor.</strong>
              <span>Atualizações vêm de {{ sale()!.externalProvider || 'integração externa' }}. Não é possível editar pela plataforma.</span>
            </div>
        </div>
      } @else if (!isStatusEditable()) {
        <div appUiPanelCard class="banner banner--warn" role="status">
            <ng-icon name="heroExclamationTriangle" size="16" />
            <div>
              <strong>Status "{{ statusLabel(sale()!.status) }}" não permite edição.</strong>
              <span>Use as ações de transição (cancelar, reembolsar, etc.) na lista de vendas.</span>
            </div>
        </div>
      } @else {
        <section appUiPanelCard class="card-panel">
          <div class="card-panel__head">
            <span appUiLabel class="mb-1 block uppercase">Detalhes</span>
            <h2 appUiPanelTitle class="mb-0 text-[1.2rem]">
              <a class="detail-link" [routerLink]="['/produtos', sale()!.productId]">{{ sale()!.productName || 'Produto' }}</a>
            </h2>
            <p appUiPanelText>
              Status: <strong>{{ statusLabel(sale()!.status) }}</strong> ·
              Lead:
              <a class="detail-link" [routerLink]="['/crm/contatos']" [queryParams]="{ open: sale()!.contactId }">{{ sale()!.contactName || sale()!.contactId.slice(0, 8) }}</a>
            </p>
          </div>

          <div class="form-grid">
            <label class="form-field">
              <span appUiLabel class="uppercase">Quantidade</span>
              <input
                appInput
                type="number"
                min="1"
                step="1"
                [ngModel]="quantity()"
                (ngModelChange)="setQuantity($event)"
                [disabled]="submitting()"
              />
            </label>

            <label class="form-field">
              <span appUiLabel class="uppercase">Validade (opcional)</span>
              <input
                type="date"
                appUiDateField
                [ngModel]="validUntil()"
                (ngModelChange)="validUntil.set($event)"
                [disabled]="submitting()"
              />
            </label>

            <div class="form-field">
              <span appUiLabel class="uppercase">Total</span>
              <p class="total-preview">{{ formatMoney(computedTotal(), sale()!.currency) }}</p>
              <small appUiHint>Subtotal × quantidade − descontos. Atualizado após salvar.</small>
            </div>
          </div>

          @if (!isDraft()) {
            <p appUiHint>
              Esta venda está em "{{ statusLabel(sale()!.status) }}". Apenas quantidade e validade podem ser editadas neste estado.
            </p>
          }

          @if (error()) {
            <div appUiPanelCard class="banner banner--error" role="alert">
              <ng-icon name="heroExclamationTriangle" size="16" />
              <span>{{ error() }}</span>
            </div>
          }

          <div class="card-panel__actions">
            <a appButton variant="ghost" routerLink="/vendas">Cancelar</a>
            <button
              appButton
              variant="primary"
              type="button"
              (click)="save()"
              [disabled]="submitting() || !hasChanges()"
            >
              <ng-icon name="heroCheck" size="14" />
              {{ submitting() ? 'Salvando…' : 'Salvar alterações' }}
            </button>
          </div>
        </section>
      }
    </section>
  `,
  styles: [
    `
      .catalog-shell {
        display: flex;
        flex-direction: column;
        gap: 1.25rem;
        padding-block: 1.5rem 3rem;
        padding-inline: var(--shell-pad-x);
      }

      .card-panel {
        display: flex;
        flex-direction: column;
        gap: 1rem;
      }

      .card-panel__actions {
        display: flex;
        justify-content: flex-end;
        gap: 0.5rem;
        padding-top: 0.75rem;
        border-top: 1px solid var(--color-line);
      }

      .form-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(14rem, 1fr));
        gap: 0.75rem;
      }

      .form-field {
        display: flex;
        flex-direction: column;
        gap: 0.3rem;
      }

      .total-preview {
        margin: 0;
        font-family: var(--font-brand);
        font-size: 1.1rem;
        font-weight: 720;
        color: var(--color-accent-strong);
      }

      .banner {
        display: flex;
        align-items: flex-start;
        gap: 0.6rem;
        font-size: 0.85rem;
      }

      .banner--warn {
        color: var(--color-warning-strong);
      }

      .banner--warn strong {
        display: block;
        font-weight: 720;
      }

      .banner--error {
        color: var(--color-tomato);
      }

      .detail-link {
        color: inherit;
        text-decoration: none;
      }

      .detail-link:hover {
        color: var(--color-accent-strong);
        text-decoration: underline;
      }
    `,
  ],
})
export class SalesEditarPage {
  private readonly api = inject(SalesApiService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly toastr = inject(ToastrService);

  readonly loading = signal(false);
  readonly submitting = signal(false);
  readonly error = signal<string | null>(null);
  readonly sale = signal<SaleListItem | null>(null);
  readonly originalQuantity = signal(1);
  readonly originalValidUntil = signal<string>('');
  readonly quantity = signal(1);
  readonly validUntil = signal<string>('');
  readonly unitAmount = signal('0');

  readonly isDraft = computed(() => this.sale()?.status === 'draft');

  readonly isStatusEditable = computed(() => {
    const s = this.sale();
    return !!s && EDITABLE_STATUSES.includes(s.status);
  });

  readonly computedTotal = computed(() => {
    const unit = Number(this.unitAmount());
    if (!Number.isFinite(unit)) return null;
    return (unit * Math.max(1, this.quantity())).toFixed(2);
  });

  readonly hasChanges = computed(() => {
    return (
      this.quantity() !== this.originalQuantity()
      || this.validUntil() !== this.originalValidUntil()
    );
  });

  constructor() {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) this.loadSale(id);
  }

  private loadSale(id: string): void {
    this.loading.set(true);
    this.api
      .getSale(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (resp) => {
          const detail = resp as unknown as SaleDetailResponse;
          this.sale.set(detail.sale);
          const first = detail.lineItems[0];
          if (first) {
            this.originalQuantity.set(first.data.quantity);
            this.quantity.set(first.data.quantity);
            this.unitAmount.set(first.data.unit_amount);
          }
          const vu = detail.sale.validUntil ? detail.sale.validUntil.slice(0, 10) : '';
          this.originalValidUntil.set(vu);
          this.validUntil.set(vu);
          this.loading.set(false);
        },
        error: (err) => {
          this.loading.set(false);
          this.error.set(err?.error?.message ?? 'Não foi possível carregar a venda.');
        },
      });
  }

  setQuantity(value: unknown): void {
    const n = Number(value);
    if (!Number.isFinite(n) || n < 1) {
      this.quantity.set(1);
      return;
    }
    this.quantity.set(Math.floor(n));
  }

  statusLabel(status: SaleStatus): string {
    return STATUS_LABEL_PT_BR[status];
  }

  formatMoney(amount: string | null, currency: string): string {
    if (!amount) return '—';
    const value = Number(amount);
    if (!Number.isFinite(value)) return amount;
    try {
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency }).format(value);
    } catch {
      return `${amount} ${currency}`;
    }
  }

  save(): void {
    const sale = this.sale();
    if (!sale || !this.hasChanges()) return;
    this.submitting.set(true);
    this.error.set(null);

    const body: { quantity?: number; valid_until?: string | null } = {};
    if (this.quantity() !== this.originalQuantity()) body.quantity = this.quantity();
    if (this.validUntil() !== this.originalValidUntil()) {
      body.valid_until = this.validUntil() ? new Date(this.validUntil()).toISOString() : null;
    }

    this.api
      .updateSale(sale.id, body)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.submitting.set(false);
          this.toastr.success('Venda atualizada.');
          this.router.navigate(['/vendas']);
        },
        error: (err) => {
          this.submitting.set(false);
          const message =
            (err?.error?.message as string | undefined) ?? 'Não foi possível salvar as alterações.';
          this.error.set(message);
        },
      });
  }
}
